"use client";

import { Clock, MessageCircle, Send, User, Paperclip } from "lucide-react";
import { Plate, usePlateEditor } from "platejs/react";
import { useState } from "react";
import { toast } from "sonner";

import type { Address, Prisma } from "@/.zenstack/logical-prisma-client";
import { EditorKit } from "@/components/editor/editor-kit";
import { ListingLink } from "@/components/ListingLink";
import { type ListingForLink } from "@/components/ListingLink";
import ApartmentDibsAvatar from "@/components/ui/ApartmentDibsAvatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Editor, EditorContainer } from "@/components/ui/editor";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/lib/auth/client";
import { useCreateReply } from "@/lib/hooks/generated/tanstack-query";
import { PlateContentRenderer } from "@/lib/plateEditorUtils";
import { getUserDisplayName } from "@/lib/utils-client";

export type ListingWithCommentsAndReplies = Prisma.ListingGetPayload<{
  select: {
    id: true;
    createdAt: true;
    description: true;
    details: true;
    user: {
      select: {
        id: true;
        name: true;
      };
    };
    comments: {
      select: {
        id: true;
        createdAt: true;
        content: true;
        user: {
          select: {
            id: true;
            name: true;
          };
        };
        replies: {
          select: {
            id: true;
            userId: true;
            content: true;
            createdAt: true;
            user: {
              select: {
                id: true;
                name: true;
              };
            };
          };
        };
        listing: {
          select: {
            id: true;
            title: true;
            status: true;
            slug: true;
          };
        };
      };
    };
  };
}> &
  ListingForLink;

type CommentWithReplies = Prisma.CommentGetPayload<{
  select: {
    id: true;
    createdAt: true;
    content: true;
    user: {
      select: {
        id: true;
        name: true;
      };
    };
    replies: {
      select: {
        id: true;
        userId: true;
        content: true;
        createdAt: true;
        user: {
          select: {
            id: true;
            name: true;
          };
        };
      };
    };
    listing: {
      select: {
        id: true;
        title: true;
        status: true;
        slug: true;
      };
    };
  };
}>;

interface CommentsProps {
  listing: ListingWithCommentsAndReplies;
  isLoading?: boolean;
  showListingDetails?: boolean;
  className?: string;
}

const initialValue = [
  {
    type: "p",
    children: [{ text: "" }],
  },
];

export function Comments({
  listing,
  isLoading,
  showListingDetails = true,
  className = "",
}: CommentsProps) {
  const { data: session } = useSession();
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState(initialValue);

  // Initialize the reply editor
  const replyEditor = usePlateEditor({
    plugins: [...EditorKit],
    value: initialValue,
  });

  // Create reply mutation
  const createReplyMutation = useCreateReply();

  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        {[1, 2].map((i) => (
          <Card key={i} className="transition-shadow hover:shadow-md">
            <CardHeader>
              <div className="space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-3 w-2/3" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const formatAddress = (address: Address) => {
    if (!address) return "Address not available";
    return `${address.streetLine1}, ${address.city}${address.state ? `, ${address.state}` : ""}`;
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - new Date(date).getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor(diffTime / (1000 * 60));

    if (diffDays > 0) {
      return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
    } else if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
    } else {
      return "Just now";
    }
  };

  const hasUnreadReplies = (
    replies: Prisma.ReplyGetPayload<{
      select: {
        userId: true;
        createdAt: true;
      };
    }>[],
  ) => {
    // Check if there are replies from tenants that the landlord hasn't responded to
    return replies.some(
      (
        reply: Prisma.ReplyGetPayload<{
          select: { userId: true; createdAt: true };
        }>,
      ) =>
        reply.userId !== session?.user?.id &&
        !replies.some(
          (
            landlordReply: Prisma.ReplyGetPayload<{
              select: { userId: true; createdAt: true };
            }>,
          ) =>
            landlordReply.userId === session?.user?.id &&
            landlordReply.createdAt > reply.createdAt,
        ),
    );
  };

  const handleReply = async (commentId: string) => {
    const content = replyEditor.api.string([]);
    if (!content.trim()) {
      toast("Please enter a message");
      return;
    }

    try {
      await createReplyMutation.mutateAsync({
        data: {
          commentId: commentId,
          userId: session!.user!.id, // guarded above
          content: replyContent,
        },
      });

      toast("Reply sent successfully!");
      setReplyContent(initialValue);
      setReplyingTo(null);
      replyEditor.tf.setValue(initialValue);
    } catch (error) {
      console.error("Error creating reply:", error);
      toast("Failed to send reply. Please try again.");
    }
  };

  if (!listing.comments || listing.comments.length === 0) {
    return (
      <div className={`py-12 text-center ${className}`}>
        <div className="mb-4 text-gray-500">
          <MessageCircle className="mx-auto mb-4 h-12 w-12 text-gray-300" />
          <h3 className="text-lg font-medium text-primary-foreground">
            No comments yet
          </h3>
          <p className="text-sm text-muted-foreground">
            Be the first to comment on this listing.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {listing.comments.map((comment: CommentWithReplies) => {
        const hasUnread = hasUnreadReplies(comment.replies);

        return (
          <Card
            key={comment.id}
            className={`transition-shadow hover:shadow-md ${
              hasUnread ? "" : ""
            }`}
          >
            {showListingDetails && (
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">{listing.title}</CardTitle>
                    <CardDescription>
                      {listing.details.address
                        ? formatAddress(listing.details.address)
                        : "Address not available"}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {hasUnread && (
                      <Badge variant="default" className="">
                        New Reply
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {comment.replies.length + 1} messages
                    </Badge>
                  </div>
                </div>
              </CardHeader>
            )}

            <CardContent className="space-y-4">
              {/* Original Comment */}
              <div className="flex items-start gap-3">
                <ApartmentDibsAvatar user={comment.user} className="h-8 w-8" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {getUserDisplayName(comment.user)}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      Tenant
                    </Badge>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <PlateContentRenderer content={comment.content} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(comment.createdAt)}
                  </div>
                </div>
              </div>

              {/* Replies */}
              {comment.replies.map((reply) => (
                <div
                  key={reply.id}
                  className={`flex items-start gap-3 ${
                    reply.userId === session?.user?.id ? "ml-8" : "mr-8"
                  }`}
                >
                  <ApartmentDibsAvatar user={reply.user} className="h-6 w-6" />
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium">
                        {getUserDisplayName(reply.user)}
                      </span>
                      <Badge variant="outline" className="text-xs">
                        {reply.userId === session?.user?.id ? "You" : "Tenant"}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <PlateContentRenderer content={reply.content} />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-2 w-2" />
                      {formatTimeAgo(reply.createdAt)}
                    </div>
                  </div>
                </div>
              ))}

              {/* Reply Form */}
              {replyingTo === comment.id ? (
                <div className="mt-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span className="text-sm font-medium">Your Reply</span>
                  </div>
                  <div className="min-h-[100px] rounded-lg border">
                    <Plate editor={replyEditor}>
                      <EditorContainer>
                        <Editor />
                      </EditorContainer>
                    </Plate>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Paperclip className="h-4 w-4" />
                        Attach Files
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyContent(initialValue);
                          replyEditor.tf.setValue(initialValue);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleReply(comment.id)}
                        disabled={createReplyMutation.isPending}
                      >
                        {createReplyMutation.isPending ? (
                          "Sending..."
                        ) : (
                          <>
                            <Send className="h-4 w-4" />
                            Send Reply
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  {showListingDetails && (
                    <Button variant="outline" className="flex-1" asChild>
                      <ListingLink listing={comment.listing}>
                        View Property
                      </ListingLink>
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setReplyingTo(comment.id)}
                  >
                    <MessageCircle className="h-4 w-4" />
                    Reply
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
